import type { Root } from "./prototypeTypes";

export function byId(root: Root, id: string) {
  return root.querySelector<HTMLElement>(`#${cssEscape(id)}`);
}

export function setText(root: Root, id: string, value: string) {
  const node = byId(root, id);
  if (node) node.textContent = value;
}

export function inputValue(root: Root, id: string) {
  return root.querySelector<HTMLInputElement>(`#${cssEscape(id)}`)?.value ?? "";
}

export function textareaValue(root: Root, id: string) {
  return root.querySelector<HTMLTextAreaElement>(`#${cssEscape(id)}`)?.value ?? "";
}

export function selectedCategory(root: Root, scopeId: string) {
  const scope = byId(root, scopeId) ?? root;
  return scope.querySelector<HTMLElement>(".idea-cat-btn.active, .idea-cat-btn.selected, .feedback-cat-btn.active, .feedback-cat-btn.selected")?.dataset.cat ?? "";
}

export function toggleClass(root: Root, id: string, className: string) {
  byId(root, id)?.classList.toggle(className);
}

export function addClass(root: Root, id: string, className: string) {
  byId(root, id)?.classList.add(className);
}

export function removeClass(root: Root, id: string, className: string) {
  byId(root, id)?.classList.remove(className);
}

export function activateSiblingButton(source: HTMLElement, className: string) {
  const parent = source.parentElement;
  if (!parent || !className) return;
  parent.querySelectorAll(`.${cssEscape(className)}`).forEach((node) => node.classList.remove("active"));
  source.classList.add("active");
}

export function matchCall(action: string, fn: string) {
  const start = action.indexOf(`${fn}(`);
  if (start < 0) return null;
  const args = action.slice(start + fn.length + 1).trimStart();
  if (args.startsWith("'")) {
    const end = args.indexOf("'", 1);
    return end > 1 ? args.slice(1, end) : null;
  }
  const number = args.match(/^\d+/);
  return number?.[0] ?? null;
}

export function matchLastQuotedArg(action: string, fn: string) {
  const start = action.indexOf(`${fn}(`);
  if (start < 0) return null;
  const args = action.slice(start + fn.length + 1, action.indexOf(")", start));
  const matches = [...args.matchAll(/'([^']+)'/g)];
  return matches.at(-1)?.[1] ?? null;
}

export function decodeHtml(value: string) {
  return new DOMParser().parseFromString(value, "text/html").documentElement.textContent ?? "";
}

export function cssEscape(value: string) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

/**
 * Minimum time (ms) a skeleton must stay visible before being swapped
 * for real content. Without this, fast API responses let a skeleton
 * flash for only a few dozen ms — which reads as a flicker, not a
 * loading state. 400ms is short enough to never delay real content
 * perceptibly when the API is slow, but long enough to make the
 * skeleton register as "loading" when the API is fast.
 */
const MIN_SKELETON_MS = 400;

let skeletonVisibleAt = 0;

/**
 * Called once when the home layout flips the sections visible (i.e.
 * when skeletons actually start being on-screen). Each hydrator then
 * calls `awaitMinSkeletonDelay()` before replacing its skeleton so
 * every block shows the skeleton for at least MIN_SKELETON_MS,
 * regardless of how fast its individual API returns.
 */
export function markSkeletonsVisible(): void {
  if (skeletonVisibleAt === 0 && typeof performance !== "undefined") {
    skeletonVisibleAt = performance.now();
  }
}

/**
 * Wait until at least MIN_SKELETON_MS has elapsed since
 * `markSkeletonsVisible()`. No-op (resolves immediately) if that
 * threshold is already past or if skeletons were never marked visible.
 */
export function awaitMinSkeletonDelay(): Promise<void> {
  if (skeletonVisibleAt === 0 || typeof performance === "undefined") {
    return Promise.resolve();
  }
  const elapsed = performance.now() - skeletonVisibleAt;
  const remaining = MIN_SKELETON_MS - elapsed;
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, remaining));
}
